import {
    Box,
    Stack,
    Switch,
    Typography
} from "@mui/material";
import React from "react";
import theme from "../../../../theme/Theme";
import { ProjectConfiguration } from "../../../../types/sd";
import CheckboxGroup from "../../../atoms/checkboxes/checkboxes";
import { attributeCheckboxes as attributeCheckboxesDefault, CodeCheckboxField, codeEditableCheckboxes } from "../defaultCQAConfig";
import "../ruleCommonSection.css";
import { SELECTION_COLOR, SWITCH_STYLE } from "../ruleCommonStyleBlocks";

interface Props {
    attributeCheckboxes?: CodeCheckboxField[];
    cqaConfig: ProjectConfiguration;
    spanAnnotationEnabled: boolean;
    setCqaConfig: (value: React.SetStateAction<ProjectConfiguration>) => void;
    selectedRubricId?: number | null;
    showLegacyAssessmentRole?: boolean
}

export const Tasks = ({
    attributeCheckboxes = attributeCheckboxesDefault,
    cqaConfig,
    spanAnnotationEnabled,
    setCqaConfig,
    selectedRubricId = null,
    showLegacyAssessmentRole = false
}: Props): JSX.Element => {
    return (
        <Box>
            {/* Container Title */}
            <Box className="setting_section_title_container">
                <Box className="setting_section_text_container" style={{ width: "53px" }}>
                    <span className="setting_section_text">
                        Tasks
                    </span>
                </Box>
            </Box>
            {/* main area */}
            <Box
                id="tasks-config"
                className='setting_section_main_container'
                sx={{ border: `1px solid ${theme.palette.artisticStroke200.main}`, minHeight: "260px", gap: "10px" }}
            >

                {/* visible and allow new codes toggles */}
                {
                    (["procedure", "modifier", "diagnosis"] as const).map((code) => (
                        <Box key={code} sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <Stack direction={"row"} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Switch
                                    data-testid={`${code}Visible`}
                                    checked={cqaConfig[code] || cqaConfig.tasks?.[code]?.visible}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        const newConfig = { ...cqaConfig };

                                        newConfig[code] = null;
                                        newConfig.tasks = {
                                            ...newConfig.tasks,
                                            [code]: {
                                                ...newConfig.tasks?.[code],
                                                visible: event.target.checked,
                                                allow_new_codes: !event.target.checked ? false : newConfig.tasks?.[code]?.allow_new_codes
                                            }
                                        };

                                        setCqaConfig(newConfig);
                                    }}
                                    sx={SWITCH_STYLE(cqaConfig[code] || cqaConfig.tasks?.[code]?.visible)}
                                />
                                <Typography className="setting_section_input_label" sx={{ width: "105px" }}>Show {code.charAt(0).toUpperCase() + code.slice(1)}</Typography>
                                {
                                    (cqaConfig[code] || cqaConfig.tasks?.[code]?.visible) && (
                                        <CheckboxGroup
                                            fields={codeEditableCheckboxes}
                                            flexDirection={"row"}
                                            values={{ allow_new_codes: cqaConfig[code] || cqaConfig.tasks?.[code]?.allow_new_codes }}
                                            onChange={(_, checked) => {
                                                const newConfig = { ...cqaConfig };

                                                newConfig[code] = null;
                                                newConfig.tasks = {
                                                    ...newConfig.tasks,
                                                    [code]: { ...newConfig.tasks?.[code], allow_new_codes: checked }
                                                };

                                                setCqaConfig(newConfig);
                                            }}
                                            stylesBox={{ gap: "24px", marginLeft: "2px" }}
                                            stylesCheckbox={SELECTION_COLOR}
                                        />
                                    )
                                }
                            </Stack>
                        </Box>
                    ))
                }

                {/* attributes section - hidden when rubric is assigned */}
                {!selectedRubricId && showLegacyAssessmentRole && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <Typography className="setting_section_input_label">Attributes</Typography>
                        <CheckboxGroup
                            fields={attributeCheckboxes}
                            values={cqaConfig}
                            onChange={(field, checked) => {
                                const newConfig = { ...cqaConfig };
                                newConfig[field] = checked;
                                setCqaConfig(newConfig);
                            }}
                            stylesBox={{ marginLeft: "2px" }}
                        />
                    </Box>
                )}

                {/* collection level section */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <Stack direction={"row"} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Switch
                            data-testid="collectionTransactionsEnabled"
                            checked={!cqaConfig["caseEvaluationLevel"]}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const newConfig = { ...cqaConfig };
                                newConfig["caseEvaluationLevel"] = !event.target.checked;
                                setCqaConfig(newConfig);
                            }}
                            sx={SWITCH_STYLE(!cqaConfig["caseEvaluationLevel"])}
                        />
                        <Typography className="setting_section_input_label" sx={{ color: "black" }}>Collect Transactions</Typography>
                    </Stack>
                    <Typography className="setting_section_input_label" variant="body2" sx={{ marginLeft: "70px", marginBottom: "10px" }}>
                        The default collection will allow graders to associate modifiers and diagnosis codes to a specific procedure and billing provider. Turn off for case level code association.
                    </Typography>
                </Box>

                {/* span annotation toggle */}
                {
                    spanAnnotationEnabled && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            <Stack direction={"row"} sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Switch
                                    data-testid="spanAnnotationEnabled"
                                    checked={Boolean(cqaConfig.spanAnnotationEnabled)}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        const newConfig = { ...cqaConfig };
                                        newConfig["spanAnnotationEnabled"] = event.target.checked;
                                        setCqaConfig(newConfig);
                                    }}
                                    sx={SWITCH_STYLE(Boolean(cqaConfig.spanAnnotationEnabled))}
                                />
                                <Typography className="setting_section_input_label" sx={{ color: "black" }}>Annotations</Typography>
                            </Stack>
                        </Box>
                    )
                }

            </Box >
        </Box>
    );
}